import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Briefcase, CalendarClock, Edit3, FileSignature, FileUp, Mail, MapPin,
  Send, Sparkles, StickyNote, Upload,
} from "lucide-react";
import { toast } from "sonner";

import { API, api, apiError, fmtDate, fmtDateTime, initials, relativeTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  AiNotice, EmptyState, Field, MatchRing, PageHeader, ScoreBar, SkillChip, StatusBadge,
} from "@/components/common/Primitives";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { SelectField, TextAreaField, TextField } from "@/components/forms/Fields";
import CandidateForm from "@/components/forms/CandidateForm";
import CandidateReport from "@/components/common/CandidateReport";
import { InterviewForm, OfferForm } from "@/components/forms/OpsForms";

const TABS = ["overview", "experience", "skills", "applications", "interviews", "offers", "documents", "communication", "notes", "activity"];

function MatchPanel({ candidateId }) {
  const [jobs, setJobs] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get(`/ai/candidate/${candidateId}/recommended-jobs`, { params: { limit: 6 } })
      .then((r) => {
        setJobs(r.data.items);
        setActive(r.data.items[0] || null);
      })
      .catch(() => setJobs([]));
  }, [candidateId]);

  if (!jobs) return <Skeleton className="h-64 rounded-md bg-slate-100" />;
  if (!jobs.length)
    return (
      <EmptyState icon={Sparkles} title="No open jobs to match against" description="Create an open requirement to see match scores." />
    );

  return (
    <div className="surface rounded-md p-6" data-testid="match-panel">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="label-xs flex items-center gap-1.5">
            <Sparkles className="h-3 w-3 text-violet-600" /> AI match intelligence
          </p>
          <h2 className="mt-1.5 text-lg font-medium text-slate-900">
            Best fit: {active?.job_title}
          </h2>
          <p className="text-xs text-slate-500">
            {active?.client_name || "Client not available"} · {active?.location || "Location not available"}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <MatchRing score={active?.score} testid="match-score-ring" />
          <StatusBadge value={active?.band} />
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="space-y-3">
          {Object.entries(active?.breakdown || {}).map(([k, v]) => (
            <ScoreBar key={k} label={k} value={v} />
          ))}
        </div>
        <div className="space-y-4">
          <div>
            <p className="label-xs mb-2">Matched skills</p>
            <div className="flex flex-wrap gap-1.5" data-testid="matched-skills">
              {active?.matched_skills?.length
                ? active.matched_skills.map((s) => <SkillChip key={s} skill={s} tone="matched" />)
                : <span className="text-xs text-slate-400">Not available</span>}
            </div>
          </div>
          <div>
            <p className="label-xs mb-2">Missing skills</p>
            <div className="flex flex-wrap gap-1.5" data-testid="missing-skills">
              {active?.missing_skills?.length
                ? active.missing_skills.map((s) => <SkillChip key={s} skill={s} tone="missing" />)
                : <span className="text-xs text-slate-400">None</span>}
            </div>
          </div>
        </div>
      </div>

      <AiNotice text="AI-generated suggestion based on structured profile data. Scores are transparent and recruiter judgement is required." />

      <div className="mt-5 border-t border-slate-200 pt-4">
        <p className="label-xs mb-3">Other recommended roles</p>
        <div className="flex flex-wrap gap-2">
          {jobs.map((j) => (
            <button
              key={j.job_id}
              onClick={() => setActive(j)}
              data-testid="recommended-job-chip"
              className={`rounded-full border px-3 py-1.5 text-[11px] transition-colors ${
                active?.job_id === j.job_id
                  ? "border-blue-400 bg-blue-50 text-blue-700"
                  : "border-slate-200 bg-slate-100 text-slate-500 hover:border-blue-300"
              }`}
            >
              {j.job_title} · <span className="font-mono-num">{j.score}%</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function CandidateDetail() {
  const { candidateId } = useParams();
  const navigate = useNavigate();
  const { meta, isStaff, isInternal, user } = useAuth();
  const [c, setC] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const [note, setNote] = useState("");
  const [edit, setEdit] = useState(false);
  const [ivOpen, setIvOpen] = useState(false);
  const [offerOpen, setOfferOpen] = useState(false);
  const [addJobOpen, setAddJobOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [docOpen, setDocOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [stage, setStage] = useState("New");
  const [status, setStatus] = useState(null);
  const [email, setEmail] = useState({ subject: "", body: "", template_key: null });
  const [templates, setTemplates] = useState([]);
  const [summary, setSummary] = useState(null);
  const [docForm, setDocForm] = useState({ doc_type: "Resume", visibility: "internal", file: null });
  const [similar, setSimilar] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/candidates/${candidateId}`);
      setC(data);
      setStatus(data.status);
    } catch (e) {
      toast.error(apiError(e, "Candidate not found"));
      navigate("/candidates");
    }
  }, [candidateId, navigate]);

  useEffect(() => {
    load();
  }, [load, refresh]);

  useEffect(() => {
    if (!isInternal) return;
    api.get("/communications/templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get(`/ai/candidate/${candidateId}/similar`, { params: { limit: 5 } })
      .then((r) => setSimilar(r.data.items)).catch(() => {});
  }, [candidateId, isInternal]);

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/candidates/${candidateId}/notes`, { body: note });
      setNote("");
      toast.success("Note added");
      setRefresh((r) => r + 1);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const addToJob = async () => {
    if (!jobId) return toast.error("Select a job");
    try {
      await api.post(`/candidates/${candidateId}/apply`, { job_id: jobId, stage });
      toast.success("Added to the job pipeline");
      setAddJobOpen(false);
      setRefresh((r) => r + 1);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const submitToClient = async () => {
    const app = c.applications?.[0];
    if (!app) return toast.error("Add this candidate to a job first");
    try {
      await api.post(`/jobs/${app.job_id}/pipeline/move`, {
        application_ids: [app.application_id || app.id], stage: "Client Submitted",
      });
      toast.success(`Submitted to ${app.client_name || "client"}`);
      setRefresh((r) => r + 1);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const sendEmail = async () => {
    try {
      await api.post("/communications?send=true", {
        candidate_id: candidateId, channel: "Email", subject: email.subject,
        body: email.body, template_key: email.template_key,
      });
      toast.success("Email sent and logged");
      setEmailOpen(false);
      setRefresh((r) => r + 1);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const changeStatus = async () => {
    try {
      await api.patch(`/candidates/${candidateId}/status`, { status });
      toast.success(`Status set to ${status}`);
      setStatusOpen(false);
      setRefresh((r) => r + 1);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const uploadDoc = async () => {
    if (!docForm.file) return toast.error("Choose a file");
    try {
      const fd = new FormData();
      fd.append("file", docForm.file);
      fd.append("owner_type", "candidate");
      fd.append("owner_id", candidateId);
      fd.append("doc_type", docForm.doc_type);
      fd.append("visibility", docForm.visibility);
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      setDocOpen(false);
      setDocForm({ doc_type: "Resume", visibility: "internal", file: null });
      setRefresh((r) => r + 1);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const genSummary = async () => {
    setSummary("loading");
    try {
      const { data } = await api.post("/ai/summary", { candidate_id: candidateId });
      setSummary(data.summary);
    } catch (e) {
      setSummary(null);
      toast.error(apiError(e));
    }
  };

  if (!c)
    return (
      <div className="space-y-6">
        <Skeleton className="h-28 bg-slate-100" />
        <Skeleton className="h-64 bg-slate-100" />
      </div>
    );

  return (
    <div className="space-y-6">
      <button onClick={() => navigate(-1)} data-testid="back-button" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-600">
        <ArrowLeft className="h-3.5 w-3.5" /> Back
      </button>

      <div className="flex flex-col gap-5 border-b border-slate-200 pb-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex gap-4">
          <span className="grid h-14 w-14 shrink-0 place-items-center rounded-md bg-blue-100 text-lg text-blue-700">
            {initials(c.full_name)}
          </span>
          <div className="min-w-0">
            <p className="font-mono-num text-[10px] text-slate-400">{c.candidate_code}</p>
            <h1 className="text-2xl font-medium tracking-tight text-slate-900 sm:text-3xl" data-testid="candidate-name">
              {c.full_name}
            </h1>
            <p className="mt-1.5 text-sm text-slate-500">
              {c.current_designation || "Role not available"}
              {c.current_company ? ` · ${c.current_company}` : ""}
            </p>
            <div className="mt-2.5 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs text-slate-500">
              <span className="font-mono-num">{c.total_experience_years ?? "?"} yrs experience</span>
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-3 w-3" /> {c.location || "Not available"}
              </span>
              <span>{c.industry || "Industry not available"}</span>
              {c.email && <span>{c.email}</span>}
              {c.phone && <span className="font-mono-num">{c.phone}</span>}
            </div>
          </div>
        </div>
        {isStaff && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setReportOpen(true)} data-testid="ai-report-button"
              className="h-9 gap-2 rounded-full border-violet-200 bg-violet-50 text-xs text-violet-700 hover:bg-violet-100">
              <Sparkles className="h-3.5 w-3.5" /> Candidate AI report
            </Button>
            <Button onClick={submitToClient} data-testid="submit-to-client-button" className="h-9 gap-2 rounded-full bg-blue-600 px-4 text-xs hover:bg-blue-500">
              <Send className="h-3.5 w-3.5" /> Submit to client
            </Button>
            <Button variant="outline" onClick={() => setIvOpen(true)} data-testid="schedule-interview-button" className="h-9 gap-2 rounded-full border-slate-200 bg-transparent text-xs text-slate-600 hover:bg-slate-100">
              <CalendarClock className="h-3.5 w-3.5" /> Schedule interview
            </Button>
            <Button variant="outline" onClick={() => setAddJobOpen(true)} data-testid="add-to-job-button" className="h-9 gap-2 rounded-full border-slate-200 bg-transparent text-xs text-slate-600 hover:bg-slate-100">
              <Briefcase className="h-3.5 w-3.5" /> Add to job
            </Button>
            <Button variant="outline" onClick={() => setEmailOpen(true)} data-testid="send-email-button" className="h-9 gap-2 rounded-full border-slate-200 bg-transparent text-xs text-slate-600 hover:bg-slate-100">
              <Mail className="h-3.5 w-3.5" /> Send email
            </Button>
            <Button variant="outline" onClick={() => setOfferOpen(true)} data-testid="create-offer-button" className="h-9 gap-2 rounded-full border-slate-200 bg-transparent text-xs text-slate-600 hover:bg-slate-100">
              <FileSignature className="h-3.5 w-3.5" /> Create offer
            </Button>
            <Button variant="outline" onClick={() => setDocOpen(true)} data-testid="upload-document-button" className="h-9 gap-2 rounded-full border-slate-200 bg-transparent text-xs text-slate-600 hover:bg-slate-100">
              <Upload className="h-3.5 w-3.5" /> Upload document
            </Button>
            <Button variant="outline" onClick={() => setEdit(true)} data-testid="edit-candidate-button" className="h-9 gap-2 rounded-full border-slate-200 bg-transparent text-xs text-slate-600 hover:bg-slate-100">
              <Edit3 className="h-3.5 w-3.5" /> Edit
            </Button>
          </div>
        )}
      </div>

      <div className="grid gap-6 xl:grid-cols-[1fr_300px]">
        <div className="min-w-0 space-y-6">
          {isInternal && <MatchPanel candidateId={candidateId} />}

          <Tabs defaultValue="overview">
            <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 border border-slate-200 bg-white p-1">
              {TABS.filter((t) => isInternal || !["notes", "activity", "communication"].includes(t)).map((t) => (
                <TabsTrigger key={t} value={t} data-testid={`tab-${t}`}
                  className="rounded px-3.5 py-1.5 text-xs capitalize data-[state=active]:bg-blue-100 data-[state=active]:text-blue-700">
                  {t}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="overview" className="mt-5 space-y-4">
              <div className="surface rounded-md p-6">
                <div className="flex items-center justify-between">
                  <p className="label-xs">Profile summary</p>
                  {isInternal && (
                    <Button size="sm" variant="ghost" onClick={genSummary} data-testid="ai-summary-button" className="h-7 gap-1.5 text-[11px] text-violet-700 hover:bg-violet-50">
                      <Sparkles className="h-3 w-3" /> AI summary
                    </Button>
                  )}
                </div>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{c.summary || "Not available"}</p>
                {summary === "loading" && <Skeleton className="mt-4 h-16 bg-slate-100" />}
                {summary && summary !== "loading" && (
                  <>
                    <p className="mt-4 rounded border border-violet-200 bg-violet-50 p-3 text-sm leading-relaxed text-slate-600" data-testid="ai-summary-text">
                      {summary}
                    </p>
                    <AiNotice />
                  </>
                )}
              </div>
              <div className="surface grid gap-5 rounded-md p-6 sm:grid-cols-3">
                <Field label="Current CTC" value={c.current_ctc} mono />
                <Field label="Expected CTC" value={c.expected_ctc} mono />
                <Field label="Notice period" value={c.notice_period} />
                <Field label="Availability" value={c.availability} />
                <Field label="Source" value={c.source} />
                <Field label="Recruiter" value={c.recruiter_name} />
                <Field label="LinkedIn" value={c.linkedin} />
                <Field label="Portfolio" value={c.portfolio} />
                <Field label="GitHub" value={c.github} />
              </div>
            </TabsContent>

            <TabsContent value="experience" className="mt-5 space-y-4">
              <div className="surface rounded-md p-6">
                <p className="label-xs mb-4">Employment history</p>
                {(c.companies || []).length === 0 ? (
                  <p className="text-sm text-slate-400">Not available</p>
                ) : (
                  <ul className="space-y-5">
                    {c.companies.map((e, i) => (
                      <li key={i} className="border-l border-slate-200 pl-4">
                        <p className="text-sm text-slate-900">{e.designation || "Role not available"}</p>
                        <p className="text-xs text-slate-500">{e.company}</p>
                        <p className="font-mono-num mt-0.5 text-[11px] text-slate-400">
                          {e.start || "?"} — {e.end || "?"}
                        </p>
                        {e.summary && <p className="mt-1.5 text-xs leading-relaxed text-slate-500">{e.summary}</p>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="surface rounded-md p-6">
                <p className="label-xs mb-4">Education</p>
                {(c.education || []).length === 0 ? (
                  <p className="text-sm text-slate-400">Not available</p>
                ) : (
                  <ul className="space-y-3">
                    {c.education.map((e, i) => (
                      <li key={i}>
                        <p className="text-sm text-slate-800">{e.degree}</p>
                        <p className="text-xs text-slate-500">
                          {e.institution || "Institution not available"} · {e.year || "year not available"}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {(c.projects || []).length > 0 && (
                <div className="surface rounded-md p-6">
                  <p className="label-xs mb-4">Projects</p>
                  <ul className="space-y-3">
                    {c.projects.map((p, i) => (
                      <li key={i}>
                        <p className="text-sm text-slate-800">{p.name}</p>
                        <p className="text-xs text-slate-500">{p.description || "Not available"}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </TabsContent>

            <TabsContent value="skills" className="mt-5 space-y-4">
              {[["Skills", c.skills], ["Technologies", c.technologies], ["Certifications", c.certifications], ["Tags", c.tags]].map(([label, list]) => (
                <div key={label} className="surface rounded-md p-6">
                  <p className="label-xs mb-3">{label}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(list || []).length ? list.map((s) => <SkillChip key={s} skill={s} />) : <span className="text-xs text-slate-400">Not available</span>}
                  </div>
                </div>
              ))}
            </TabsContent>

            <TabsContent value="applications" className="mt-5">
              {(c.applications || []).length === 0 ? (
                <EmptyState icon={Briefcase} title="Not on any pipeline yet" description="Add this candidate to a job to start tracking progress." />
              ) : (
                <div className="surface divide-y divide-slate-200 rounded-md">
                  {c.applications.map((a) => (
                    <button key={a.id} onClick={() => navigate(`/jobs/${a.job_id}`)} data-testid="application-row"
                      className="flex w-full items-center justify-between gap-4 p-4 text-left hover:bg-slate-100">
                      <div className="min-w-0">
                        <p className="truncate text-sm text-slate-900">{a.job_title}</p>
                        <p className="truncate text-xs text-slate-500">
                          {a.client_name || "Client not available"} · {a.job_code}
                        </p>
                        <p className="mt-1 text-[10px] text-slate-400">Updated {relativeTime(a.updated_at)}</p>
                      </div>
                      <div className="flex shrink-0 items-center gap-3">
                        {a.match_score != null && (
                          <span className="font-mono-num text-xs text-slate-500">{a.match_score}%</span>
                        )}
                        <StatusBadge value={a.stage} />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="interviews" className="mt-5">
              {(c.interviews || []).length === 0 ? (
                <EmptyState icon={CalendarClock} title="No interviews scheduled" description="Interview rounds and feedback appear here." />
              ) : (
                <div className="surface divide-y divide-slate-200 rounded-md">
                  {c.interviews.map((iv) => (
                    <div key={iv.id} className="p-4" data-testid="interview-row">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-sm text-slate-900">{iv.round_name} · {iv.interview_type}</p>
                          <p className="text-xs text-slate-500">{iv.job_title}</p>
                          <p className="mt-1 font-mono-num text-[11px] text-slate-400">{fmtDateTime(iv.scheduled_at)}</p>
                        </div>
                        <StatusBadge value={iv.status} />
                      </div>
                      {iv.feedback && (
                        <div className="mt-3 rounded border border-slate-200 bg-slate-50 p-3">
                          <div className="flex flex-wrap gap-4 text-[11px] text-slate-500">
                            <span>Technical {iv.feedback.technical_skills}/5</span>
                            <span>Communication {iv.feedback.communication}/5</span>
                            <span>Problem solving {iv.feedback.problem_solving}/5</span>
                            <span>Overall {iv.feedback.overall_rating}/5</span>
                            <StatusBadge value={iv.feedback.recommendation} />
                          </div>
                          {iv.feedback.strengths && (
                            <p className="mt-2 text-xs text-slate-500">Strengths: {iv.feedback.strengths}</p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="offers" className="mt-5">
              {(c.offers || []).length === 0 ? (
                <EmptyState icon={FileSignature} title="No offers raised" description="Offers and joining status will show here." />
              ) : (
                <div className="surface divide-y divide-slate-200 rounded-md">
                  {c.offers.map((o) => (
                    <div key={o.id} className="flex items-start justify-between gap-4 p-4" data-testid="offer-row">
                      <div>
                        <p className="text-sm text-slate-900">{o.job_title}</p>
                        <p className="font-mono-num text-xs text-slate-500">{o.offered_ctc} LPA</p>
                        <p className="mt-1 text-[11px] text-slate-400">Joining {fmtDate(o.joining_date)}</p>
                      </div>
                      <div className="space-y-1.5 text-right">
                        <StatusBadge value={o.status} />
                        {o.joining_status && <StatusBadge value={o.joining_status} />}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="documents" className="mt-5">
              {(c.documents || []).length === 0 ? (
                <EmptyState icon={FileUp} title="No documents on file" description="Upload a resume, ID or offer letter." />
              ) : (
                <div className="surface divide-y divide-slate-200 rounded-md">
                  {c.documents.map((d) => (
                    <div key={d.id} className="flex items-center justify-between gap-4 p-4" data-testid="document-row">
                      <div className="min-w-0">
                        <p className="truncate text-sm text-slate-800">{d.name}</p>
                        <p className="text-[11px] text-slate-400">
                          {d.doc_type} · v{d.version} · {d.uploaded_by_name} · {fmtDate(d.created_at)}
                        </p>
                      </div>
                      {d.storage_path ? (
                        <Button size="sm" variant="outline" onClick={() => window.open(`${API}/documents/${d.id}/download`, "_blank")}
                          className="h-8 rounded-full border-slate-200 bg-transparent text-xs text-slate-600">
                          Preview
                        </Button>
                      ) : (
                        <span className="text-[11px] text-slate-400">Demo record</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="communication" className="mt-5">
              {(c.communications || []).length === 0 ? (
                <EmptyState icon={Mail} title="No communication logged" description="Emails, calls and notes appear on this timeline." />
              ) : (
                <ul className="surface divide-y divide-slate-200 rounded-md">
                  {c.communications.map((m) => (
                    <li key={m.id} className="p-4" data-testid="communication-row">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm text-slate-800">{m.subject || m.channel}</p>
                        <StatusBadge value={m.channel} />
                      </div>
                      <p className="mt-1.5 whitespace-pre-line text-xs leading-relaxed text-slate-500">{m.body}</p>
                      <p className="mt-1.5 text-[10px] text-slate-400">
                        {m.actor_name} · {relativeTime(m.created_at)}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </TabsContent>

            <TabsContent value="notes" className="mt-5">
              <div className="surface space-y-4 rounded-md p-6">
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="candidate-note-input"
                  placeholder="Add an internal note — never visible to clients or candidates…"
                  className="border-slate-200 bg-slate-50 text-sm text-slate-800" />
                <Button size="sm" onClick={addNote} data-testid="candidate-note-submit" className="h-8 gap-2 rounded-full bg-blue-600 text-xs hover:bg-blue-500">
                  <StickyNote className="h-3 w-3" /> Add note
                </Button>
                {(c.notes || []).length === 0 ? (
                  <p className="text-xs text-slate-400">No notes yet.</p>
                ) : (
                  <ul className="divide-y divide-slate-200">
                    {[...c.notes].reverse().map((n) => (
                      <li key={n.id} className="py-4" data-testid="candidate-note-item">
                        <p className="text-sm text-slate-600">{n.body}</p>
                        <p className="mt-1.5 text-[11px] text-slate-400">{n.author_name} · {relativeTime(n.created_at)}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </TabsContent>

            <TabsContent value="activity" className="mt-5">
              <div className="surface rounded-md p-6">
                {(c.activity || []).length === 0 ? (
                  <p className="text-xs text-slate-400">No activity recorded.</p>
                ) : (
                  <ul className="space-y-4">
                    {c.activity.map((a) => (
                      <li key={a.id} className="flex gap-3">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />
                        <div>
                          <p className="text-xs text-slate-600">
                            <span className="text-slate-900">{a.user_name}</span> {a.action}
                          </p>
                          <p className="mt-0.5 text-[10px] text-slate-400">{relativeTime(a.created_at)}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <aside className="space-y-4">
          <div className="surface space-y-4 rounded-md p-5">
            <div>
              <p className="label-xs mb-2">Current status</p>
              <div className="flex items-center gap-2">
                <StatusBadge value={c.status} testid="candidate-status-badge" />
                {isStaff && (
                  <button onClick={() => setStatusOpen(true)} data-testid="change-status-button" className="text-[11px] text-blue-600 hover:text-blue-700">
                    Change
                  </button>
                )}
              </div>
            </div>
            <Field label="Pipeline stage" value={c.current_stage} />
            <Field label="Applications" value={c.application_count} mono />
            <Field label="Best match score" value={c.best_match_score ? `${c.best_match_score}%` : null} mono />
            <Field label="Recruiter" value={c.recruiter_name} />
            <Field label="Added" value={fmtDate(c.created_at)} />
          </div>

          {isInternal && similar.length > 0 && (
            <div className="surface rounded-md p-5">
              <p className="label-xs mb-3">Similar candidates</p>
              <ul className="space-y-3">
                {similar.map((s) => (
                  <li key={s.candidate_id}>
                    <button onClick={() => navigate(`/candidates/${s.candidate_id}`)} data-testid="similar-candidate"
                      className="w-full text-left hover:text-blue-700">
                      <span className="flex items-center justify-between gap-2">
                        <span className="truncate text-xs text-slate-800">{s.name}</span>
                        <span className="font-mono-num text-[10px] text-slate-500">{s.similarity}%</span>
                      </span>
                      <span className="block truncate text-[10px] text-slate-400">
                        {s.designation || "Role not available"} · {s.experience ?? "?"} yrs
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>

      <CandidateForm open={edit} onOpenChange={setEdit} candidate={c} onSaved={() => setRefresh((r) => r + 1)} />
      <CandidateReport
        open={reportOpen}
        onOpenChange={setReportOpen}
        candidateId={candidateId}
        applications={c.applications || []}
        defaultJobId={c.applications?.[0]?.job_id}
      />
      <InterviewForm open={ivOpen} onOpenChange={setIvOpen} defaults={{ candidate_id: candidateId }} onSaved={() => setRefresh((r) => r + 1)} />
      <OfferForm open={offerOpen} onOpenChange={setOfferOpen} defaults={{ candidate_id: candidateId }} onSaved={() => setRefresh((r) => r + 1)} />

      <Dialog open={addJobOpen} onOpenChange={setAddJobOpen}>
        <DialogContent className="max-w-md border-slate-200 bg-white">
          <DialogHeader>
            <DialogTitle className="text-base text-slate-900">Add to a job pipeline</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <SelectField label="Job" value={jobId} onChange={setJobId} testid="add-job-select"
              options={(meta?.jobs || []).map((j) => ({ value: j.id, label: `${j.job_code} · ${j.title}` }))} />
            <SelectField label="Starting stage" value={stage} onChange={setStage} options={meta?.enums?.pipeline_stages || []} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddJobOpen(false)} className="text-slate-500">Cancel</Button>
            <Button onClick={addToJob} data-testid="add-job-confirm" className="rounded-full bg-blue-600 px-5 hover:bg-blue-500">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="max-w-xl border-slate-200 bg-white">
          <DialogHeader>
            <DialogTitle className="text-base text-slate-900">Email {c.full_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <SelectField label="Template" value={email.template_key} testid="email-template-select"
              onChange={(k) => {
                const t = templates.find((x) => x.key === k);
                setEmail({
                  template_key: k,
                  subject: (t?.subject || "").replace("{job_title}", c.applications?.[0]?.job_title || "your application"),
                  body: (t?.body || "").replace("{candidate_name}", c.full_name)
                    .replace("{recruiter_name}", user?.name || "")
                    .replace("{job_title}", c.applications?.[0]?.job_title || "the role")
                    .replace("{client_name}", c.applications?.[0]?.client_name || "our client"),
                });
              }}
              options={templates.map((t) => ({ value: t.key, label: t.name }))} />
            <TextField label="Subject" value={email.subject} onChange={(v) => setEmail((e) => ({ ...e, subject: v }))} testid="email-subject-input" />
            <TextAreaField label="Message" value={email.body} onChange={(v) => setEmail((e) => ({ ...e, body: v }))} rows={8} testid="email-body-input" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEmailOpen(false)} className="text-slate-500">Cancel</Button>
            <Button onClick={sendEmail} data-testid="email-send-button" className="rounded-full bg-blue-600 px-5 hover:bg-blue-500">Send email</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={docOpen} onOpenChange={setDocOpen}>
        <DialogContent className="max-w-md border-slate-200 bg-white">
          <DialogHeader>
            <DialogTitle className="text-base text-slate-900">Upload a document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <SelectField label="Document type" value={docForm.doc_type} onChange={(v) => setDocForm((f) => ({ ...f, doc_type: v }))}
              options={meta?.enums?.doc_types || []} testid="doc-type-select" />
            <SelectField label="Visibility" value={docForm.visibility} onChange={(v) => setDocForm((f) => ({ ...f, visibility: v }))}
              options={[{ value: "internal", label: "Internal only" }, { value: "client", label: "Visible to client" }]} />
            <input type="file" data-testid="doc-file-input" onChange={(e) => setDocForm((f) => ({ ...f, file: e.target.files?.[0] }))}
              className="w-full rounded border border-slate-200 bg-slate-50 p-2 text-xs text-slate-500" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDocOpen(false)} className="text-slate-500">Cancel</Button>
            <Button onClick={uploadDoc} data-testid="doc-upload-confirm" className="rounded-full bg-blue-600 px-5 hover:bg-blue-500">Upload</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={statusOpen} onOpenChange={setStatusOpen}>
        <DialogContent className="max-w-sm border-slate-200 bg-white">
          <DialogHeader>
            <DialogTitle className="text-base text-slate-900">Change candidate status</DialogTitle>
          </DialogHeader>
          <SelectField label="Status" value={status} onChange={setStatus} options={meta?.enums?.candidate_statuses || []} testid="status-select" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setStatusOpen(false)} className="text-slate-500">Cancel</Button>
            <Button onClick={changeStatus} data-testid="status-confirm" className="rounded-full bg-blue-600 px-5 hover:bg-blue-500">Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
